import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faCoins, faHandshake, faSackDollar, faUsers } from "@fortawesome/free-solid-svg-icons"
import List from "../../../components/List/List"
import { AdminListCard, AdminListMetricItem } from "../components/AdminListCard"
import { AdminMetricCard } from "../components/AdminMetricCard"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { AdminReseller } from "../../../Objects/AdminReseller/AdminReseller"
import { useAdminReseller } from "../../../hooks/AdminDashboard/useAdminReseller"
import { AdminResellerDetailsPopup } from "../AdminResellerDetailsPopup"

export const ResellersSection: React.FC = () => {
  const { t } = useTranslation("admin")

  const { handleGetAllResellers } = useAdminReseller()

  const [resellers, setResellers] = useState<AdminReseller[]>([])

  const [selectedResellerId, setSelectedResellerId] = useState<number | null>(null)

  const refreshResellers = () => handleGetAllResellers(setResellers)

  useEffect(() => {
    refreshResellers()
  }, [])

  return (
    <div className="w-full overflow-auto bg-white p-1 md:p-0">
      <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2 2xl:grid-cols-4 min-[1800px]:grid-cols-5">
        <AdminMetricCard
          title={t("reseller_resellers_count")}
          value={`${resellers.length}`}
          icon={faHandshake}
          tone="brand"
        />

        <AdminMetricCard
          title={t("reseller_status_active")}
          value={`${resellers.filter((item) => item.status === EEnableStatusType.Active).length}`}
          icon={faUsers}
          tone="positive"
        />

        <AdminMetricCard
          title={t("reseller_column_commission_paid")}
          value={convertToLocalMoney(resellers.reduce((sum, item) => sum + item.commissionPaid, 0))}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("reseller_column_commission_pending")}
          value={convertToLocalMoney(resellers.reduce((sum, item) => sum + item.commissionPending, 0))}
          icon={faCoins}
          tone="warning"
        />
      </div>

      <div className="p-2">
        <List
          messageCount={t("reseller_resellers_count")}
          messageEmpty={t("reseller_resellers_empty")}
          nameFindSearch={t("reseller_resellers_search")}
          handleRefreshButton={refreshResellers}
          handleOnClick={setSelectedResellerId}
          data={resellers.map((item, index) => {
            const isActive = item.status === EEnableStatusType.Active

            return {
              id: item.resellerId,
              index,
              title: item.name,
              linkImage: "",
              isDisable: !isActive,
              dataFilter: [item.name, item.referralCode, item.email ?? ""],
              children: (
                <AdminListCard
                  title={item.name}
                  subtitle={item.referralCode}
                  badge={isActive ? t("reseller_status_active") : t("reseller_status_inactive")}
                  badgeClass={isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}
                  cardClass={
                    isActive
                      ? "rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3"
                      : "rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                  }
                >
                  <div className="gap-2">
                    <AdminListMetricItem
                      label={t("reseller_column_active_clients")}
                      value={`${item.activeClients}/${item.totalClients}`}
                    />

                    <AdminListMetricItem
                      label={t("reseller_column_commission_paid")}
                      value={convertToLocalMoney(item.commissionPaid)}
                      accent="text-emerald-700"
                    />

                    <AdminListMetricItem
                      label={t("reseller_column_commission_pending")}
                      value={convertToLocalMoney(item.commissionPending)}
                      accent="text-amber-700"
                    />

                    <AdminListMetricItem
                      label={t("reseller_column_commission_plan")}
                      value={item.currentCommissionPlanName ?? t("reseller_no_plan_assigned")}
                    />
                  </div>
                </AdminListCard>
              ),
            }
          })}
        />
      </div>

      {selectedResellerId !== null && (
        <AdminResellerDetailsPopup
          resellerId={selectedResellerId}
          onClose={() => {
            setSelectedResellerId(null)
            refreshResellers()
          }}
        />
      )}
    </div>
  )
}
